import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const T = 'pages.collaborators.import';

const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadBlob = (content, filename, type) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

export function OrgImportWizard({ open, onClose, onImported, mode = 'client', presetCompany = null }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [step, setStep] = useState('upload');           // upload | preview | result
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    if (open && mode === 'hm') {
      if (presetCompany?.id) { setCompanyId(presetCompany.id); return; }
      api.get('/admin/clients').then((r) => setCompanies(r.data || [])).catch(() => setCompanies([]));
    }
  }, [open, mode, presetCompany?.id]);

  const reset = () => { setStep('upload'); setFile(null); setPreview(null); setResult(null); setCompanyId(presetCompany?.id || ''); };
  const close = () => { reset(); onClose(); };
  const errLabel = (code) => t(`${T}.errCodes.${code}`, { defaultValue: code });

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get('/client/import/org/template', { responseType: 'blob' });
      downloadBlob(data, 'CTH_import_departments_employees.xlsx',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (mode === 'hm') fd.append('company_id', companyId);
      const { data } = await api.post('/client/import/org/preview', fd);
      setPreview(data);
      setStep('preview');
    } catch (e) {
      toast.error(t(`${T}.invalidFile`), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/client/import/org/confirm', {
        departments_new: preview.departments_new,
        employees_new: preview.employees_new,
        ...(mode === 'hm' ? { company_id: companyId } : {}),
      });
      // Surface rows skipped during validation on the result screen (amber state).
      setResult({ ...data, errors: [...(preview.errors || []), ...(data.errors || [])] });
      setStep('result');
      onImported?.();
    } catch (e) {
      toast.error(t(`${T}.failed`), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };

  const downloadErrorReport = () => {
    const rows = [['sheet', 'row', 'column', 'error'],
      ...(preview?.errors || []).map((e) => [e.sheet, e.row, e.column, errLabel(e.error)])];
    downloadBlob(rows.map((r) => r.map(csvCell).join(',')).join('\n'), 'import_validation_report.csv', 'text/csv');
  };

  const downloadCreds = () => {
    const rows = [['name', 'email', 'temp_password'],
      ...(result?.credentials || []).map((c) => [c.name, c.email, c.temp_password])];
    downloadBlob(rows.map((r) => r.map(csvCell).join(',')).join('\n'), 'temporary_credentials.csv', 'text/csv');
  };

  const Stat = ({ label, value, tone }) => (
    <div className={`rounded-lg border p-3 text-center ${tone === 'err' && value ? 'border-red-300 bg-red-50' : 'border-border/60'}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl" data-testid="org-import-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />{t(`${T}.title`)}
          </DialogTitle>
          <DialogDescription>{t(`${T}.subtitle`)}</DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            {mode === 'hm' && (
              <div className="space-y-1.5">
                <p className="text-sm font-medium">{t(`${T}.selectCompany`)}</p>
                {presetCompany?.id ? (
                  <div className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm font-medium"
                       data-testid="import-company-preset">
                    <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                    {presetCompany.name}
                  </div>
                ) : (
                  <Select value={companyId} onValueChange={setCompanyId}>
                    <SelectTrigger data-testid="import-company-select">
                      <SelectValue placeholder={t(`${T}.selectCompanyPh`)} />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            <Button variant="outline" onClick={downloadTemplate} className="gap-1.5 w-full" data-testid="import-download-template">
              <Download className="h-4 w-4" /> {t(`${T}.downloadTemplate`)}
            </Button>
            <div
              className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50"
              onClick={() => fileRef.current?.click()}
              data-testid="import-dropzone"
            >
              <Upload className="h-7 w-7 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">{file ? file.name : t(`${T}.uploadFile`)}</p>
              <p className="text-xs text-muted-foreground mt-1">.xlsx</p>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="import-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <Stat label={t(`${T}.newDepts`)} value={preview.departments_new.length} />
              <Stat label={t(`${T}.existingDepts`)} value={preview.departments_existing.length} />
              <Stat label={t(`${T}.newEmployees`)} value={preview.employees_new.length} />
              <Stat label={t(`${T}.existingEmployees`)} value={preview.employees_existing.length} />
              <Stat label={t(`${T}.usersToCreate`)} value={preview.users_to_create} />
              <Stat label={t(`${T}.errors`)} value={preview.errors.length} tone="err" />
            </div>
            {preview.errors.length > 0 && (
              <div className="border border-red-200 rounded-lg max-h-44 overflow-auto" data-testid="import-errors">
                <table className="w-full text-xs">
                  <thead className="bg-red-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left">{t(`${T}.sheet`)}</th>
                      <th className="px-2 py-1.5 text-left">{t(`${T}.row`)}</th>
                      <th className="px-2 py-1.5 text-left">{t(`${T}.column`)}</th>
                      <th className="px-2 py-1.5 text-left">{t(`${T}.error`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.errors.map((e, i) => (
                      <tr key={i} className="border-t border-red-100">
                        <td className="px-2 py-1">{e.sheet}</td>
                        <td className="px-2 py-1">{e.row || '—'}</td>
                        <td className="px-2 py-1">{e.column}</td>
                        <td className="px-2 py-1">{errLabel(e.error)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {preview.errors.length > 0 && (
              <Button variant="outline" size="sm" onClick={downloadErrorReport} className="gap-1.5" data-testid="import-download-report">
                <Download className="h-3.5 w-3.5" /> {t(`${T}.downloadReport`)}
              </Button>
            )}
            <p className="text-xs text-muted-foreground">{t(`${T}.previewNote`)}</p>
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-4" data-testid="import-result">
            <div className={`flex items-center gap-2 rounded-lg p-3 ${result.errors.length ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>
              {result.errors.length
                ? <AlertTriangle className="h-5 w-5" />
                : <CheckCircle2 className="h-5 w-5" />}
              <p className="text-sm font-semibold">
                {result.errors.length ? t(`${T}.importWithErrors`) : t(`${T}.importSuccess`)}
              </p>
            </div>
            <div className="text-sm space-y-1">
              <div>{t(`${T}.newDepts`)}: <Badge variant="secondary">{result.departments_created.length}</Badge></div>
              <div>{t(`${T}.newEmployees`)}: <Badge variant="secondary">{result.employees_created.length}</Badge></div>
              <div>{t(`${T}.usersToCreate`)}: <Badge variant="secondary">{result.credentials.length}</Badge></div>
              {result.errors.length > 0 && (
                <div className="text-amber-700">{t(`${T}.errors`)}: <Badge variant="secondary">{result.errors.length}</Badge></div>
              )}
            </div>
            {result.credentials.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-amber-700 font-medium">{t(`${T}.credsNote`)}</p>
                <Button variant="outline" size="sm" onClick={downloadCreds} className="gap-1.5" data-testid="import-download-creds">
                  <Download className="h-3.5 w-3.5" /> {t(`${T}.downloadCreds`)}
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={close}>{t('common.cancel')}</Button>
              <Button onClick={doPreview} disabled={!file || busy || (mode === 'hm' && !companyId)} className="gap-1.5" data-testid="import-validate-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t(`${T}.validate`)}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>{t(`${T}.back`)}</Button>
              <Button
                onClick={doImport}
                disabled={busy || (preview.departments_new.length === 0 && preview.employees_new.length === 0)}
                className="gap-1.5"
                data-testid="import-confirm-btn"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t(`${T}.import`)}
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button onClick={close} data-testid="import-done-btn">{t(`${T}.done`)}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
